import getSymbolFromCurrency from "currency-symbol-map";
import sortBy from "lodash.sortby";
import { v4 } from "uuid";

import {
  AudienceFilterDto,
  BaseIntegrationDto,
  ConditionType,
  CreateAudienceDto,
  CreateCustomEventDto,
  CreateExclusionGroupEntityFe,
  CreateExclusionGroupFe,
  CreateExperienceAnalysis,
  CreateExperienceIntegrationDto,
  CreateExperienceKeyMetricDto,
  DiscountApplicationMethodType,
  DiscountApplicationType,
  DiscountTierDto,
  ExperienceCategory,
  ExperiencePageTargetingFe,
  ExperienceType,
  ExpressionNodeType,
  IntegrationName,
  OfferDto,
  RateConditionType,
  RateGroupType,
  RateType,
  RedirectDto,
  RedirectFilter,
  ShippingRateFe,
  ShippingRateGroupFe,
  ShopifyCurrencyType,
  ShopifyTemplateTypeType,
  TabEnumFe,
  TabType,
} from "@intelligems/ig-types";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import {
  VariationFe,
  VariationFeProps,
} from "@intelligems/ig-types/fe-only/variations";

export const DISCOUNT_MAX_UNITS = 1000000000;

export const getDefaultExperienceName = (
  hasTestPricing: boolean,
  hasTestShipping: boolean,
  hasTestCampaign: boolean,
  title?: string
): string => {
  const pricing = "Pricing";
  const shipping = "Shipping";
  const campaign = "Campaign";
  const content = "Content";

  if (title) {
    if (!title.includes(pricing) && hasTestPricing) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        `${title} + ${pricing}`
      );
    } else if (!title.includes(shipping) && hasTestShipping) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        `${title} + ${shipping}`
      );
    } else if (!title.includes(campaign) && hasTestCampaign) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        `${title} + ${campaign}`
      );
    } else {
      return `${title} Test`;
    }
  } else {
    if (hasTestPricing) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        pricing
      );
    } else if (hasTestShipping) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        shipping
      );
    } else if (hasTestCampaign) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        campaign
      );
    } else {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        content
      );
    }
  }
};

export const initialFrontendProps = (options: {
  name: string;
}): VariationFeProps => {
  return {
    label: options.name,
  };
};

export const initialAudienceState = (
  priceTestDefaultCurrency?: string
): CreateAudienceDto => {
  return {
    id: v4(),
    enabled: true,
    excludeCurrency: {
      exclude: !!priceTestDefaultCurrency,
      currency: priceTestDefaultCurrency || null,
      country: undefined,
      region: undefined,
      city: undefined,
    },
    wholesale: false,
    audienceType: "common",
    elseAction: null,
    elseVariationId: null,
    elseExcludeFromAnalytics: false,
    filters: [],
    evaluationFrequency: null,
  };
};

export const initialAudienceFiltersRolloutsGenerate = (
  expression: ExpressionNodeType[]
): AudienceFilterDto => {
  return {
    id: v4(),
    priority: 0,
    filterType: "utm",
    action: null,
    excludeFromAnalytics: false,
    expressionType: "custom",
    expression,
  };
};
export const initialAudienceFilterState = (): AudienceFilterDto => {
  return {
    id: v4(),
    priority: 0,
    filterType: "utm",
    action: null,
    excludeFromAnalytics: false,
    expressionType: "common",
    expression: [
      {
        query: {
          key: "",
          value: "",
          filter: "equals",
          type: "utm",
        },
      },
    ],
  };
};
export const initialPageTargetingState = (
  experienceId?: string
): ExperiencePageTargetingFe => {
  return {
    id: v4(),
    value: "",
    order: 0,
    filterType: "url",
    experienceId,
  };
};
export const initialRedirectState = ({
  variation,
  group,
  isThemeTest = false,
  templateType,
  isTemplateTest = false,
  copyRedirect,
  redirectFilter,
  skip,
  templateSuffixes = [],
}: {
  variation: VariationFe;
  group: number;
  isThemeTest: boolean;
  templateType?: ShopifyTemplateTypeType | null;
  isTemplateTest?: boolean;
  copyRedirect?: RedirectDto;
  redirectFilter?: RedirectFilter;
  skip?: boolean;
  templateSuffixes?: string[];
}): RedirectDto => {
  return {
    id: v4(),
    group,
    originUrl: copyRedirect?.originUrl ?? "",
    queryParams: [],
    destinationUrl: copyRedirect?.destinationUrl ?? "",
    skip: skip ?? (variation.isControl && !isTemplateTest && !isThemeTest),
    variationId: variation.id,
    isThemeTest,
    isTemplateTest:
      isTemplateTest !== undefined ? isTemplateTest : !!templateType,
    templateType: templateType,
    templateSuffixes: templateSuffixes,
    redirectOnce: copyRedirect?.redirectOnce ?? false,
    filter: copyRedirect?.filter ?? redirectFilter ?? "none",
    find: undefined,
    redirectType: copyRedirect?.redirectType ?? "fullReplacement",
  };
};

export const offerTestDiscountTiersVariationInitial = (
  existingTiers: DiscountTierDto[]
): DiscountTierDto[] => {
  return sortBy(
    existingTiers.map((tier) => {
      return {
        ...tier,
        id: v4(),
      };
    }),
    (_) => _.maximumUnits
  );
};

export const initialDiscountTierState = (
  discountApplicationType?: DiscountApplicationType
): DiscountTierDto[] => {
  const baseTier = {
    minimumUnits: 0,
    maximumUnits: DISCOUNT_MAX_UNITS,
    unitDiscount: 0,
    discountTitle: null,
    giftWithPurchaseTitle: null,
    isFreeShipping: discountApplicationType === "freeShipping",
    isGiftWithPurchase: false,
    autoAddGiftWithPurchase: true,
  };

  if (discountApplicationType === "tieredDiscount") {
    return [
      {
        id: v4(),
        ...baseTier,
        minimumUnits: 2,
      },
      {
        id: v4(),
        ...baseTier,
        minimumUnits: 4,
      },
    ];
  }

  return [
    {
      id: v4(),
      ...baseTier,
    },
  ];
};

export const initialExperienceAnalysis = (options: {
  testFlowType: ExperienceType;
  useWideApp: boolean;
  defaultCurrency?: string;
  hasTestPricing: boolean;
  hasTestShipping: boolean;
  hasTestCampaign: boolean;
  hasTestContent: boolean;
  hasTestContentUrl: boolean;
  hasTestContentAdvanced: boolean;
  hasTestContentOnsite: boolean;
  hasTestContentTemplate: boolean;
  hasTestContentTheme: boolean;
  hasTestOnsiteInjections: boolean;
}): CreateExperienceAnalysis => {
  return {
    chosenWinnerVariationId: null,
    impact: null,
    orders: null,
    impactWinnerVariationId: null,
    excludeFromImpact: false,
    analysisUrl: "",
    estimatedTestLengthRange: { high: null, low: null },
    visitors: null,
    analyticsViewType: options.hasTestPricing
      ? "test_orders_only"
      : options.hasTestShipping ||
          options.hasTestCampaign ||
          (!options.hasTestCampaign &&
            !options.hasTestShipping &&
            !options.hasTestPricing)
        ? "sitewide_orders"
        : "test_orders_only",
    goal: "revenue",
  };
};

const getExperienceIntegrations = (
  testFlowType: ExperienceType,
  integrations: BaseIntegrationDto[]
): CreateExperienceIntegrationDto[] => {
  const EXPERIENCE_INTEGRATIONS: readonly IntegrationName[] = [
    "StayAi",
    "Recharge",
  ];
  if (testFlowType !== "pricing") return [];
  return (
    integrations
      .filter((integration) =>
        EXPERIENCE_INTEGRATIONS.includes(integration.name)
      )
      .map((integration) => ({
        integration: {
          name: integration.name,
          description: integration.description,
          apis: integration.apis,
          globalSettings: integration.globalSettings,
          enabled: integration.enabled,
        },
        enabled: integration.name === "StayAi" ? false : integration.enabled,
        experienceSettings: {},
      })) ?? []
  );
};

// this is for experiment
export const initialExperienceFeDto = (options: {
  testFlowType: ExperienceType;
  useWideApp: boolean;
  defaultCurrency?: string;
  hasTestPricing: boolean;
  hasTestShipping: boolean;
  hasTestCampaign: boolean;
  hasTestContent: boolean;
  hasTestContentUrl: boolean;
  hasTestContentAdvanced: boolean;
  hasTestContentOnsite: boolean;
  hasTestContentTemplate: boolean;
  hasTestContentTheme: boolean;
  hasTestOnsiteInjections: boolean;
  category?: ExperienceCategory;
  name?: string;
  description?: string;
  tab?: TabType;
  integrations?: BaseIntegrationDto[] | undefined;
}): ExperienceFe => {
  return {
    id: undefined,
    isPreview: true,
    category: options.category ?? "experiment",
    viewMode: "edit",
    actionMode: "create",
    tab: options.tab
      ? options.tab
      : options.category === "experiment"
        ? TabEnumFe.TEST_GROUPS
        : TabEnumFe.MODS,
    productsLocation: null,
    name: options.name ?? "",
    description: options.description ?? "",
    status: "pending",
    experienceActions: [],
    isExternal: false,
    testTypes: {
      hasTestPricing: options.hasTestPricing,
      hasTestShipping: options.hasTestShipping,
      hasTestCampaign: options.hasTestCampaign,
      hasTestContent: options.hasTestContent,
      hasTestContentUrl: options.hasTestContentUrl,
      hasTestContentAdvanced: options.hasTestContentAdvanced,
      hasTestContentOnsite: options.hasTestContentOnsite,
      hasTestContentTemplate: options.hasTestContentTemplate,
      hasTestContentTheme: options.hasTestContentTheme,
      hasTestOnsiteInjections: options.hasTestOnsiteInjections,
    },
    variations: [],
    experiencePageTargeting: [],
    experienceIntegrations: options.integrations
      ? getExperienceIntegrations(options.testFlowType, options.integrations)
      : [],
    experienceAnalysis: initialExperienceAnalysis(options),
    experienceCustomMetrics: [],
    requiresLink: false,
    linkBaseUrl: null,
    devicePreview: {
      device: "desktop",
      path: "/",
    },
    shippingTestMethodDefinitions: [],
    type: options.testFlowType,
    startedAtTs: null,
    endedAtTs: null,
    pausedAtTs: null,
    productVariantCount: 0,
    currency:
      options.hasTestPricing && options.defaultCurrency
        ? {
            code: options.defaultCurrency,
            symbol: getSymbolFromCurrency(options.defaultCurrency) || "$",
          }
        : null,
    isNew: true,
  };
};

export const offerTestOfferVariationInitial = ({
  existingOffer,
  variationId,
  variationName,
  id,
}: {
  existingOffer: OfferDto;
  variationId: string;
  variationName: string;
  id: string;
}): OfferDto => {
  return {
    id,
    enabled: false,
    isTest: true,
    name: `Discount Copy of ${variationName}`,
    unitType: existingOffer.unitType,
    discountType: existingOffer.discountType,
    discountApplicationType: existingOffer.discountApplicationType,
    discountApplicationMethod: existingOffer.discountApplicationMethod,
    tiers: offerTestDiscountTiersVariationInitial(existingOffer.tiers),
    isArchived: false,
    combinesWithIntelligems: existingOffer.combinesWithIntelligems,
    combinesWithShopify: existingOffer.combinesWithShopify,
    shouldRejectCodes: existingOffer.shouldRejectCodes,
    variationId,
    shippingDiscount: existingOffer.shippingDiscount
      ? { ...existingOffer.shippingDiscount, id: v4() }
      : null,
  };
};

export const initialOfferState = ({
  totalCount,
  isTest,
  variationId,
  discountApplicationType,
  discountApplicationMethod,
}: {
  totalCount?: number;
  isTest?: boolean;
  variationId: string;
  discountApplicationType?: DiscountApplicationType;
  discountApplicationMethod?: DiscountApplicationMethodType;
}): OfferDto => {
  return {
    id: v4(),
    enabled: false,
    isTest: isTest || false,
    name: totalCount ? `Discount ${totalCount + 1}` : `Discount`,
    unitType: "unit",
    discountType: "percentage",
    discountApplicationType: discountApplicationType || "",
    discountApplicationMethod: discountApplicationMethod || "perItem",
    tiers: initialDiscountTierState(discountApplicationType),
    isArchived: false,
    combinesWithIntelligems: true,
    combinesWithShopify: true,
    shouldRejectCodes: false,
    variationId,
    shippingDiscount:
      discountApplicationType === "freeShipping"
        ? {
            id: v4(),
            discountType: "percentage",
            unitDiscount: 100,
            countryFilter: null,
            rateFilter: null,
            discountLabel: null,
          }
        : null,
  };
};

export const initialTierShippingState = (
  tierType: RateConditionType,
  unitType: ConditionType
) => {
  return {
    min: null,
    max: null,
    conditionType: tierType,
    unit: unitType,
  };
};

export const initialShippingRateState = (
  storeCurrency: ShopifyCurrencyType,
  rateType: RateType,
  selectedRateType: RateGroupType,
  tierType: RateConditionType,
  unitType: ConditionType
): ShippingRateFe => {
  return {
    name: "",
    amount: 0,
    rateType: rateType,
    currency: storeCurrency,
    condition:
      selectedRateType === "tieredByPriceOrWeight"
        ? initialTierShippingState(tierType, unitType)
        : undefined,
  };
};

export const initialShippingRateGroupsState = (
  selectedRateType: RateGroupType,
  storeCurrency: ShopifyCurrencyType,
  tierType: RateConditionType,
  unitType: ConditionType,
  rateGroupPosition: number,
  variationId: string
): ShippingRateGroupFe => {
  const types: RateType[] = [];

  if (selectedRateType === "flatRate") {
    types.push("amount");
  } else if (selectedRateType === "flatRateWithThreshold") {
    types.push("amount", "threshold");
  } else if (selectedRateType === "thresholdOnly") {
    types.push("threshold");
  } else {
    types.push("tier");
  }
  return {
    groupType: selectedRateType,
    position: rateGroupPosition,
    variationId,
    rates:
      selectedRateType === "custom"
        ? []
        : types.map((rate) =>
            initialShippingRateState(
              storeCurrency,
              rate,
              selectedRateType,
              tierType,
              unitType
            )
          ),
  };
};

export const initialSingleVariationState = (): VariationFe => {
  return {
    id: v4(),
    isControl: true,
    name: "Control Group",
    order: 0,
    percentage: 100,
    priceChange: undefined,
    priceChangeUnit: "percent",
    shippingRateGroups: [],
    excludeProducts: false,
    includeProducts: false,
    functionSyncError: false,
    ...initialFrontendProps({
      name: "Control Group",
    }),
  };
};

export const initialVariationState = (): VariationFe[] => {
  return [
    {
      id: v4(),
      isControl: true,
      name: "Control Group",
      order: 0,
      percentage: 50,
      priceChange: undefined,
      priceChangeUnit: "percent",
      shippingRateGroups: [],
      excludeProducts: false,
      includeProducts: false,
      functionSyncError: false,
      ...initialFrontendProps({
        name: "Control Group",
      }),
    },
    {
      id: v4(),
      isControl: false,
      name: "New Group 1",
      order: 1,
      percentage: 50,
      priceChange: undefined,
      functionSyncError: false,
      priceChangeUnit: "percent",
      excludeProducts: false,
      includeProducts: false,
      shippingRateGroups: [],
      ...initialFrontendProps({
        name: "New Group 1",
      }),
    },
  ];
};

export const initialVariation = (
  id: string,
  maxOrder: number,
  variations: VariationFe[]
): VariationFe => {
  let newName = `New Group ${maxOrder + 1}`;

  if (variations.some((variation) => variation.name === newName)) {
    newName = `New Group ${maxOrder + 2}`;
  }
  return {
    id,
    name: newName,
    isControl: false,
    order: maxOrder + 1,
    percentage: 100 / (variations.length + 1),
    priceChange: undefined,
    priceChangeUnit: "percent",
    shippingRateGroups: [],
    label: `New Group ${maxOrder + 1}`,
    priceRoundingType: "none",
    priceRoundingAmount: "0",
    excludeProducts: false,
    includeProducts: false,
    functionSyncError: false,
  };
};

export const initialExclusionGroupEntityStateFunc = (
  entityId?: string,
  order?: number
): CreateExclusionGroupEntityFe => {
  return {
    percentage: 0,
    order: order ?? 0,
    hasPriorityOrder: false,
    experienceId: entityId,
  };
};

export const initialExclusionGroupStateFunc = (
  entityId?: string
): CreateExclusionGroupFe => {
  return {
    name: "",
    exclusionGroupEntities: [initialExclusionGroupEntityStateFunc(entityId)],
    reassignOnEnd: true,
    hasWeightedValues: false,
  };
};

export const initPageViewEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "pageView",
  identifier: "",
  settings: {},
  applyToUrls: [],
});

export const initProductPageViewEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "productPageView",
  identifier: "",
  settings: [
    {
      productId: "",
      handle: "",
    },
  ],
});

export const initCollectionPageViewEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "collectionPageView",
  identifier: "",
  settings: [
    {
      handle: "",
    },
  ],
});

export const initClickEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "clickEvent",
  identifier: "",
  settings: {
    selectors: [],
  },
  applyToUrls: [],
});

export const initElementViewedEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "elementViewed",
  identifier: "",
  settings: {
    selectors: [],
  },
  applyToUrls: [],
});

export const initScrollDepthEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "scrollDepth",
  identifier: "",
  settings: {
    mobile: { percentage: 50 },
    desktop: { percentage: 50 },
  },
  applyToUrls: [],
});

export const initJavascriptEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "javascriptEvent",
  identifier: "",
  settings: {
    code: "",
  },
  applyToUrls: [],
});

export const initJavascriptImportedEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "javascriptImportedEvent",
  identifier: "",
  settings: {
    registered: false,
  },
});

export const initExperienceKeyMetrics = (
  shouldShowProfit: boolean
): CreateExperienceKeyMetricDto[] => {
  return [
    {
      order: 0,
      standardEventId: "conversionRate",
      isPrimary: false,
    },
    {
      order: 1,
      standardEventId: "netRevenuePerVisitor",
      isPrimary: !shouldShowProfit,
    },
    {
      order: 2,
      standardEventId: "profitPerVisitor",
      isPrimary: shouldShowProfit,
    },
    {
      order: 3,
      standardEventId: "netRevenuePerOrder",
      isPrimary: false,
    },
  ];
};

export const initialCustomEventStates = {
  pageView: initPageViewEvent(),
  productPageView: initProductPageViewEvent(),
  collectionPageView: initCollectionPageViewEvent(),
  clickEvent: initClickEvent(),
  elementViewed: initElementViewedEvent(),
  scrollDepth: initScrollDepthEvent(),
  javascriptEvent: initJavascriptEvent(),
  javascriptImportedEvent: initJavascriptImportedEvent(),
};
